/**
 * Photo capture -> recognition, in one self-managing screen. Only
 * `confirm`/`cancel` reach `app.ts` — every intermediate step (capture,
 * recognizing, failure) is handled by re-rendering this file, same
 * convention as `settings.ts`'s validation-error re-renders.
 *
 * Deliberately no separate "review the recognized hand" screen: a
 * successful recognition goes straight to `actions.confirm`, landing on the
 * caller's own pre-filled tile-grid screen, which already has a "Scan
 * Photo…" button to retake and is the real correction surface either way —
 * an extra screen with its own Retake/Continue buttons in between was pure
 * friction. The one thing that screen did that's worth keeping is passed
 * along instead: which tiles recognition wasn't confident about, so the
 * caller can still flag them (see `PhotoCaptureResult.needsReviewTiles`).
 */

import { checkOriginalFileSize, downscaleImageFile } from '../imageUtils';
import { getSessionToken } from '../activation';
import { renderBrandHeader } from '../branding';
import { esc } from '../helpers';
import { isTile } from '../../rules/tiles';
import type { BonusTile, Tile } from '../../rules/tiles';
import type { Meld } from '../../rules/types';
import { logRecognitionAttempt } from '../usageLog';
import {
  RecognitionError,
  recogniseTiles,
  type RecognitionFailureKind,
} from '../../vision/client';
import { toDraft, type CorrectionDraft } from '../../vision/correction';
import { clearPendingScan, savePendingScan } from '../../vision/pendingScan';
import type { RecogniseResponse } from '../../vision/client';
import { getRecognitionEngine, type RecognitionEngine } from '../recognitionEngine';
import { logRecognitionMiss, notePendingRecognition } from '../recognitionAccuracy';
import { renderPhotoCrop } from './photoCrop';

/**
 * Dynamic, not static, import — onDeviceRecognition.ts pulls in
 * @tensorflow/tfjs, which alone is ~1.8MB unminified (~300KB gzipped).
 * A static import would put that in every page's main bundle, even for
 * players who only ever enter scores manually. This way it's its own chunk,
 * fetched only once someone actually opens this screen.
 */
function loadOnDeviceModule() {
  return import('../../vision/onDeviceRecognition');
}

export interface PhotoCaptureResult {
  concealed: Tile[];
  melds: Meld[];
  flowers: BonusTile[];
  photos: string[];
  /** Tiles recognition wasn't confident about — the caller shows these as a "double-check against the photo" callout on its own tile-grid screen. */
  needsReviewTiles: Tile[];
  /** The positions in `concealed` recognition was unsure about, so a hand the player had already entered can settle them (see vision/mergeScan.ts). */
  needsReviewIndices: number[];
}

export interface PhotoCaptureActions {
  confirm(result: PhotoCaptureResult): void;
  cancel(): void;
}

/**
 * Cost control, not a security check: each recognition call is a real, paid
 * vision-API request, so an admin-configured cap keeps one round from
 * burning an unbounded number of them. `onAttempt` fires right before every
 * real API call (including retries) so the caller (app.ts) can persist the
 * running per-round count even if this screen is cancelled before finishing.
 */
export interface ScanLimit {
  maxAttempts: number;
  attemptsUsed: number;
  onAttempt: () => void;
}

type Step =
  | { kind: 'capture'; error?: string }
  | { kind: 'crop'; photo: string }
  | { kind: 'recognizing'; photo: string; controller: AbortController }
  | { kind: 'no-hand'; photo: string }
  | { kind: 'failed'; photo: string; message: string };

const FAILURE_MESSAGES: Record<RecognitionFailureKind, string> = {
  insufficient_credit:
    'Out of API credit — the recognition service can\'t be reached right now. Add credit, or enter this hand manually.',
  rate_limited: 'Too many requests right now — wait a moment and try again, or enter this hand manually.',
  other: 'Recognition failed. Try again, choose a different photo, or enter this hand manually.',
};

export function renderPhotoCapture(
  root: HTMLElement,
  tableId: string,
  actions: PhotoCaptureActions,
  scanLimit: ScanLimit,
  resumePhoto?: string,
): void {
  // Warms up the on-device model in the background so it's likely already
  // downloaded by the time a photo is actually taken — safe to call every
  // time this screen mounts, loadModel() dedupes concurrent/repeat calls.
  // Skipped when the relay is the selected engine, so switching to it
  // doesn't still cost players a ~12MB background download they won't use.
  if (getRecognitionEngine() === 'on-device') {
    void loadOnDeviceModule().then((m) => m.preloadOnDeviceModel());
  }

  if (resumePhoto) {
    // Recovering a scan already in flight when the app was interrupted — the
    // relay call this represents was already paid for (or about to be), so
    // this goes straight to the relay rather than trying on-device recognition
    // fresh; it's not a new, free attempt. It still reports itself via
    // onAttempt so the tally stays accurate if the user cancels partway through.
    startRelayRecognition(resumePhoto, root, tableId, actions, scanLimit);
    return;
  }
  render({ kind: 'capture' }, root, tableId, actions, scanLimit);
}

function render(
  step: Step,
  root: HTMLElement,
  tableId: string,
  actions: PhotoCaptureActions,
  scanLimit: ScanLimit,
): void {
  switch (step.kind) {
    case 'capture':
      return renderCapture(step, root, tableId, actions, scanLimit);
    case 'crop':
      return renderPhotoCrop(root, step.photo, {
        confirm: (croppedPhoto) => {
          savePendingScan(tableId, croppedPhoto);
          startRecognition(croppedPhoto, root, tableId, actions, scanLimit);
        },
        cancel: () => render({ kind: 'capture' }, root, tableId, actions, scanLimit),
      });
    case 'recognizing':
      return renderRecognizing(step, root, tableId, actions, scanLimit);
    case 'no-hand':
      return renderTerminal(
        step.photo,
        'No hand was recognized in this photo — try a clearer, closer shot, or enter this hand manually.',
        root,
        tableId,
        actions,
        scanLimit,
      );
    case 'failed':
      return renderTerminal(step.photo, step.message, root, tableId, actions, scanLimit);
  }
}

/**
 * Shows "take the photo like this" as real reference photos — one arranged
 * in two rows (shown first, since it's the easier shape to fit in a
 * landscape photo without rotating anything) and one in a single row (also
 * fine, just needs more width). Used to show an illustrated tile-icon
 * diagram alongside it as a second candidate — Indra compared both live and
 * asked to keep just the real photo.
 */
function renderPhotoGuide(): string {
  return `
    <div class="photo-guide">
      <p class="photo-guide-label">Real example — two rows</p>
      <img
        class="photo-guide-real"
        src="/photo-guide-example-two-rows.jpg"
        alt="Example photo of a winning hand split into two rows for scanning"
      />
      <p class="muted photo-guide-caption">
        Two shorter rows, stacked — the easiest way to fit a full hand in a landscape photo.
      </p>

      <p class="photo-guide-label">Real example — one row</p>
      <img
        class="photo-guide-real"
        src="/photo-guide-example-one-row.jpg"
        alt="Example photo of a winning hand laid out in a single row for scanning"
      />
      <p class="muted photo-guide-caption">Also fine — one row works just as well if it comfortably fills the frame.</p>

      <div class="suggestion-box photo-guide-tips">
        <p style="margin:0 0 0.3rem;font-weight:600">Avoid:</p>
        <ul style="margin:0 0 0.5rem;padding-left:1.1rem">
          <li>The wall, discards, or other players' tiles in frame — just your own hand</li>
          <li>A tilted or upright row — keep every tile flat, like the examples above</li>
        </ul>
        <p style="margin:0">Doesn't fit in one row? Split it into two shorter rows instead — the order doesn't matter.</p>
      </div>
    </div>
  `;
}

function renderCapture(
  step: { error?: string },
  root: HTMLElement,
  tableId: string,
  actions: PhotoCaptureActions,
  scanLimit: ScanLimit,
): void {
  const limitReached = scanLimit.attemptsUsed >= scanLimit.maxAttempts;

  root.innerHTML = `
    ${renderBrandHeader()}
    <div class="card">
      <h1>Scan a Photo</h1>
      <p class="muted">Take or upload a photo of just the winning hand — nothing else on the table. Lay the tiles flat, in one row or two, get close enough to fill the frame, straight-on, in good light.</p>

      ${renderPhotoGuide()}

      ${step.error ? `<div class="error-box">${esc(step.error)}</div>` : ''}

      ${
        limitReached
          ? `<div class="error-box">
               Scan limit reached for this round (${scanLimit.maxAttempts} attempt${scanLimit.maxAttempts === 1 ? '' : 's'}).
               Please enter this hand manually below.
             </div>`
          : ''
      }

      <div class="row" style="margin-top:0.5rem">
        <button type="button" id="takePhotoBtn" ${limitReached ? 'disabled' : ''}>Take Photo</button>
        <button type="button" id="uploadPhotoBtn" class="secondary" ${limitReached ? 'disabled' : ''}>Upload Photo</button>
      </div>
      <input type="file" id="takePhotoInput" accept="image/*" capture="environment" style="display:none" />
      <input type="file" id="uploadPhotoInput" accept="image/*" style="display:none" />

      <button type="button" id="cancelBtn" class="ghost" style="width:100%;margin-top:1rem">
        ← Back &amp; Enter Manually
      </button>
    </div>
  `;

  const takeInput = root.querySelector<HTMLInputElement>('#takePhotoInput')!;
  const uploadInput = root.querySelector<HTMLInputElement>('#uploadPhotoInput')!;
  root.querySelector<HTMLButtonElement>('#takePhotoBtn')!.addEventListener('click', () => takeInput.click());
  root.querySelector<HTMLButtonElement>('#uploadPhotoBtn')!.addEventListener('click', () => uploadInput.click());

  const handleFile = async (file: File | undefined) => {
    if (!file) return;
    const sizeCheck = checkOriginalFileSize(file);
    if (!sizeCheck.ok) {
      render({ kind: 'capture', error: sizeCheck.error }, root, tableId, actions, scanLimit);
      return;
    }
    let photo: string;
    try {
      photo = await downscaleImageFile(file);
    } catch {
      render(
        { kind: 'capture', error: 'Could not read that image — try a different photo.' },
        root,
        tableId,
        actions,
        scanLimit,
      );
      return;
    }
    render({ kind: 'crop', photo }, root, tableId, actions, scanLimit);
  };

  takeInput.addEventListener('change', () => void handleFile(takeInput.files?.[0]));
  uploadInput.addEventListener('change', () => void handleFile(uploadInput.files?.[0]));

  root.querySelector<HTMLButtonElement>('#cancelBtn')!.addEventListener('click', () => {
    clearPendingScan(tableId);
    actions.cancel();
  });
}

/**
 * Entry point for a fresh photo. Routes to whichever engine is currently
 * selected (see recognitionEngine.ts) — a manual operator choice, not an
 * automatic per-photo fallback. Deliberately NOT "try on-device, fall back
 * to relay if weak": that would make it impossible to tell how either
 * engine is actually doing on its own, since every scan would silently
 * pick whichever route happened to produce a result. A resumed in-flight
 * scan (see renderPhotoCapture) always goes to the relay regardless of the
 * current engine setting, since that specific attempt was already paid for.
 */
function startRecognition(
  photo: string,
  root: HTMLElement,
  tableId: string,
  actions: PhotoCaptureActions,
  scanLimit: ScanLimit,
): void {
  if (getRecognitionEngine() === 'relay') {
    startRelayRecognition(photo, root, tableId, actions, scanLimit);
    return;
  }
  startOnDeviceRecognition(photo, root, tableId, actions, scanLimit);
}

/**
 * A successful recognition's only remaining stop before the caller's own
 * tile-grid screen. Every recognized tile — suspected-kong or not — is
 * carried forward as one flat pile in `concealed`, `melds` always empty:
 * the operator sorts any real claimed sets and picks the winning tile on
 * that screen, the same tile grid a fully manual entry uses, pre-filled
 * instead of empty.
 */
function confirmFromDraft(
  engine: RecognitionEngine,
  photo: string,
  draft: CorrectionDraft,
  tableId: string,
  actions: PhotoCaptureActions,
): void {
  const concealed = [...draft.tiles, ...draft.melds.flatMap((m) => m.tiles)];
  const needsReviewTiles = [
    ...new Set(
      draft.needsReview
        .map((i) => draft.tiles[i])
        .filter((t): t is Tile => t !== undefined && isTile(t)),
    ),
  ];
  clearPendingScan(tableId);
  // What this scan proposed, kept until the round is saved so it can be
  // compared with the hand as finally corrected (see recognitionAccuracy.ts).
  notePendingRecognition(photo, {
    engine,
    timestamp: Date.now(),
    tiles: concealed,
    flowers: draft.flowers,
    flagged: needsReviewTiles,
  });
  actions.confirm({
    concealed,
    melds: [],
    flowers: draft.flowers,
    photos: [photo],
    needsReviewTiles,
    needsReviewIndices: draft.needsReview.filter((i) => draft.tiles[i] !== undefined),
  });
}

function startOnDeviceRecognition(
  photo: string,
  root: HTMLElement,
  tableId: string,
  actions: PhotoCaptureActions,
  scanLimit: ScanLimit,
): void {
  const controller = new AbortController();
  render({ kind: 'recognizing', photo, controller }, root, tableId, actions, scanLimit);

  let failed = false;
  loadOnDeviceModule()
    .then((m): Promise<RecogniseResponse | undefined> => m.recogniseTilesOnDevice(photo))
    .catch(() => {
      failed = true;
      return undefined;
    })
    .then((response) => {
      if (controller.signal.aborted) return;

      const hand = response?.hands[0];
      if (!hand) {
        logRecognitionMiss('on-device', failed ? 'error' : 'no-hand');
        // Same terminal screen the relay path uses for "nothing found" —
        // no automatic retry via the other engine, by design (see doc
        // comment on startRecognition above).
        render({ kind: 'no-hand', photo }, root, tableId, actions, scanLimit);
        return;
      }
      confirmFromDraft('on-device', photo, toDraft(hand), tableId, actions);
    });
}

function startRelayRecognition(
  photo: string,
  root: HTMLElement,
  tableId: string,
  actions: PhotoCaptureActions,
  scanLimit: ScanLimit,
): void {
  // Defense in depth — the capture/terminal screens already disable their
  // buttons once the limit is hit, so this should be unreachable in normal
  // use, but never let a stale render slip an extra paid call through.
  if (scanLimit.attemptsUsed >= scanLimit.maxAttempts) {
    render({ kind: 'capture' }, root, tableId, actions, scanLimit);
    return;
  }

  const token = getSessionToken();
  if (!token) {
    // Shouldn't happen — the whole app is gated before reaching this screen
    // — but a session can still expire/get kicked between page load and
    // this action, so fail cleanly rather than send an unauthenticated call.
    render({ kind: 'failed', photo, message: FAILURE_MESSAGES.other }, root, tableId, actions, scanLimit);
    return;
  }

  scanLimit.attemptsUsed += 1;
  scanLimit.onAttempt();

  const controller = new AbortController();
  render({ kind: 'recognizing', photo, controller }, root, tableId, actions, scanLimit);

  recogniseTiles({ images: [photo], tableId }, token, controller.signal)
    .then((response) => {
      if (controller.signal.aborted) return;
      logRecognitionAttempt({
        timestamp: Date.now(),
        imageCount: 1,
        outcome: 'success',
        inputTokens: response.usage?.inputTokens,
        outputTokens: response.usage?.outputTokens,
      });
      const hand = response.hands[0];
      if (!hand) {
        logRecognitionMiss('relay', 'no-hand');
        render({ kind: 'no-hand', photo }, root, tableId, actions, scanLimit);
        return;
      }
      confirmFromDraft('relay', photo, toDraft(hand), tableId, actions);
    })
    .catch((err: unknown) => {
      if (controller.signal.aborted) return;
      const kind: RecognitionFailureKind = err instanceof RecognitionError ? err.kind : 'other';
      logRecognitionAttempt({ timestamp: Date.now(), imageCount: 1, outcome: kind });
      render({ kind: 'failed', photo, message: FAILURE_MESSAGES[kind] }, root, tableId, actions, scanLimit);
    });
}

function renderRecognizing(
  step: { photo: string; controller: AbortController },
  root: HTMLElement,
  tableId: string,
  actions: PhotoCaptureActions,
  scanLimit: ScanLimit,
): void {
  root.innerHTML = `
    ${renderBrandHeader()}
    <div class="card">
      <h1>Recognizing…</h1>
      <p class="muted">Reading tiles from the photo. This usually takes a few seconds.</p>
      <button type="button" id="cancelRecognizeBtn" class="secondary" style="width:100%;margin-top:1rem">
        Cancel
      </button>
    </div>
  `;
  root.querySelector<HTMLButtonElement>('#cancelRecognizeBtn')!.addEventListener('click', () => {
    step.controller.abort();
    render({ kind: 'capture' }, root, tableId, actions, scanLimit);
  });
}

function renderTerminal(
  photo: string,
  message: string,
  root: HTMLElement,
  tableId: string,
  actions: PhotoCaptureActions,
  scanLimit: ScanLimit,
): void {
  const limitReached = scanLimit.attemptsUsed >= scanLimit.maxAttempts;

  root.innerHTML = `
    ${renderBrandHeader()}
    <div class="card">
      <h1>Recognition Failed</h1>
      <div class="error-box">${esc(message)}</div>

      ${
        limitReached
          ? `<div class="error-box">
               Scan limit reached for this round (${scanLimit.maxAttempts} attempt${scanLimit.maxAttempts === 1 ? '' : 's'}).
               Please enter this hand manually below.
             </div>`
          : ''
      }

      <div class="stack" style="margin-top:1rem">
        <button type="button" id="retryBtn" ${limitReached ? 'disabled' : ''}>Try Again</button>
        <button type="button" id="differentPhotoBtn" class="secondary" ${limitReached ? 'disabled' : ''}>Choose Different Photo</button>
        <button type="button" id="manualBtn" class="ghost">← Back &amp; Enter Manually</button>
      </div>
    </div>
  `;
  root.querySelector<HTMLButtonElement>('#retryBtn')!.addEventListener('click', () => {
    startRecognition(photo, root, tableId, actions, scanLimit);
  });
  root.querySelector<HTMLButtonElement>('#differentPhotoBtn')!.addEventListener('click', () => {
    render({ kind: 'capture' }, root, tableId, actions, scanLimit);
  });
  root.querySelector<HTMLButtonElement>('#manualBtn')!.addEventListener('click', () => {
    clearPendingScan(tableId);
    actions.cancel();
  });
}

